import { useState } from 'react';

import { Box, Button, FieldSet, FormHelp, Input, Legend, Text, TextButton } from '@zillow/constellation-10';

import type { PhoneVerificationSource } from '@zg-rentals/ts-rental-manager-api-beta-client';
import { useRentalManagerUserPhoneVerificationWebApiHandler_validateVerificationCode } from '@zg-rentals/ts-rental-manager-api-beta-client-react-query';

import StringConstants from '../../../../../srcLegacy/shared/constants/StringConstants';
import { VERIFICATION_STEPS } from '../constants';

import numberUtils from '../../../../../srcLegacy/shared/utils/numberUtils';

import VerificationBodyTemplate from './VerificationBodyTemplate';
import MaxAttemptsError from './MaxAttemptsError';

const VERIFICATION_CODE_LENGTH = 5;

const CONFIG = {
  value: (val: string) => numberUtils.formatPhoneNumberInput(val),
  editButtonText: 'Edit number',
};

type Props = {
  contentValue: string;
  currentStepIndex: number;
  incrementStep: (step?: string) => void;
  isContentEditable?: boolean;
  verificationType: string;
  onVerificationConfirmation?: (phoneNumber: string) => Promise<void>;
  verificationSource: PhoneVerificationSource;
};

type ErrorState = { code?: string; message?: string };

const VerificationCodeSent = ({
  contentValue,
  currentStepIndex,
  incrementStep,
  isContentEditable = true,
  onVerificationConfirmation,
  verificationSource,
}: Props) => {
  const { mutateAsync: validateVerificationCode } =
    useRentalManagerUserPhoneVerificationWebApiHandler_validateVerificationCode();

  const [error, setError] = useState<ErrorState>({});
  const [verificationCode, setVerificationCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const isButtonDisabled = error?.code === StringConstants.SS_ERRORS.attempt_limit_reached;

  const confirmVerification = () => {
    if (verificationCode.length < VERIFICATION_CODE_LENGTH) {
      setError({ message: 'Enter a 5 digit verification code.' });
      return;
    }
    setIsLoading(true);
    setError({});
    return validateVerificationCode({
      getParams: {
        phone: contentValue,
        verificationCode,
        verificationSource,
      },
    })
      .then(() => {
        if (onVerificationConfirmation) {
          return onVerificationConfirmation(contentValue).then(() => incrementStep());
        }
        incrementStep();
        return undefined;
      })
      .catch((err: { response?: { data?: { message?: string; code?: string } }; message?: string }) => {
        const message = err?.response?.data?.message ?? err?.message ?? 'There was an error verifying your code.';
        const code = err?.response?.data?.code;
        setError({ code, message });
      })
      .finally(() => setIsLoading(false));
  };

  const handleGetNewCodeClick = () => {
    incrementStep(VERIFICATION_STEPS.GET_VERIFICATION_CODE);
  };

  const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, VERIFICATION_CODE_LENGTH);
    setVerificationCode(value);
    setError({});
    if (value.length === VERIFICATION_CODE_LENGTH) {
      // Allow submit on complete
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && verificationCode.length === VERIFICATION_CODE_LENGTH) {
      confirmVerification();
    }
  };

  const renderErrorMessage = () => {
    const message = error?.message ?? 'There was an error verifying your code.';
    if (isButtonDisabled) {
      return <MaxAttemptsError verificationMode="phone" />;
    }
    return (
      <FormHelp error={true} css={{ marginTop: '400', fontSize: 'body-sm' }}>
        {message}
      </FormHelp>
    );
  };

  return (
    <VerificationBodyTemplate
      heading={`Step ${currentStepIndex + 1} - Verification code sent`}
      subheading={
        <Text className="fs-mask">
          We sent the 5 digit code to
          {` ${CONFIG.value(contentValue)}. `}
          {isContentEditable && (
            <TextButton
              tone="brand"
              onClick={() => incrementStep(VERIFICATION_STEPS.ADD_CONTENT)}
              css={{
                fontSize: 'body-sm',
                fontWeight: 400,
                textDecoration: 'underline',
                xl: { fontSize: 'body' },
              }}
            >
              {CONFIG.editButtonText}
            </TextButton>
          )}
        </Text>
      }
    >
      <Box css={{ display: 'flex', alignItems: 'center', flexDirection: 'column' }}>
        <FieldSet
          legend={
            <Legend
              css={{
                textStyle: { base: 'body-sm-bold', lg: 'body-bold' },
                display: 'block',
                marginBottom: { base: '200', lg: '400' },
              }}
            >
              Enter the verification code below.
            </Legend>
          }
          css={{ margin: '{spacing.400} auto 0' }}
        >
          <Input
            data-testid="VerificationCodeValidator"
            inputMode="numeric"
            maxLength={VERIFICATION_CODE_LENGTH}
            placeholder="00000"
            value={verificationCode}
            onChange={handleCodeChange}
            onKeyDown={handleKeyDown}
            css={{ width: '120px', margin: '0 auto', display: 'block', textAlign: 'center' }}
            aria-label="Verification code"
          />
          <Box css={{ marginTop: '400', marginBottom: '600' }}>
            <Text textStyle="body-xs">
              Didn&#39;t receive the code?&nbsp;
              <TextButton tone="brand" disabled={isLoading} onClick={handleGetNewCodeClick}>
                <TextButton.Label css={{ textDecoration: 'underline', textStyle: 'body-xs-bold' }}>
                  Get new code
                </TextButton.Label>
              </TextButton>
            </Text>
          </Box>
          <Button
            tone="brand"
            emphasis="filled"
            disabled={isButtonDisabled}
            loading={isLoading}
            onClick={confirmVerification}
            css={{ minWidth: '200px', display: 'block', margin: '0 auto' }}
          >
            Check code
          </Button>
        </FieldSet>
      </Box>
      {Boolean(error?.message) && (
        <Box css={{ marginBlock: '200', display: 'flex', justifyContent: 'center' }} aria-live="assertive">
          {renderErrorMessage()}
        </Box>
      )}
    </VerificationBodyTemplate>
  );
};

export default VerificationCodeSent;
